import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Users, Eye, X, Phone, MapPin, Building2, Trash2, KeyRound, ExternalLink, Pencil, Send } from "lucide-react";
import AdminPhotoThumb from "../components/AdminPhotoThumb";
import MerchantFeatureAccess from "../components/MerchantFeatureAccess";
import MerchantOrgPanel, { orgSummary } from "../components/MerchantOrgPanel";
import { ChannelPicker, DEFAULT_CHANNELS, resultToast } from "../components/AdminChannelPicker";

const AdminMerchants = () => {
  const [searchParams] = useSearchParams();
  const [merchants, setMerchants] = useState([]);
  const [org, setOrg] = useState({});
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [rich, setRich] = useState(null);
  const [editName, setEditName] = useState(null);
  const [adminMsg, setAdminMsg] = useState("");
  const [adminMsgChannels, setAdminMsgChannels] = useState(DEFAULT_CHANNELS);
  const [thread, setThread] = useState([]);
  const [broadcast, setBroadcast] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);

  const sendBroadcast = async () => {
    setBroadcasting(true);
    try {
      const selectedEmails = (broadcast.selected || []).map((id) => merchants.find((m) => m.id === id)?.email).filter(Boolean).join(",");
      const r = await api.post("/admin/broadcast", {
        message: broadcast.message.trim(), target: broadcast.target,
        city: broadcast.city || null, emails: broadcast.target === "emails" ? selectedEmails : null,
        channels: broadcast.channels,
      });
      toast.success(r.data.message);
      setBroadcast(null);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setBroadcasting(false); }
  };
  const [detailPhotos, setDetailPhotos] = useState([]);
  const [detailPhotosLoading, setDetailPhotosLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/merchants");
      setMerchants(r.data);
      api.get("/admin/org-overview").then((o) => setOrg(o.data?.merchants || {})).catch(() => {});
      const target = searchParams.get("merchant");
      if (target) {
        const m = r.data.find((x) => x.id === target);
        if (m) view(m);
      }
    } catch (e) { toast.error(arabicError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const view = async (m) => {
    setDetail(m);
    setRich(null);
    setEditName(null);
    setAdminMsg("");
    setThread([]);
    setDetailPhotos([]);
    setDetailPhotosLoading(true);
    api.get(`/admin/merchants/${m.id}/details`).then((r) => setRich(r.data)).catch(() => {});
    api.get(`/admin/merchants/${m.id}/messages`).then((r) => setThread(r.data)).catch(() => {});
    try {
      const r = await api.get(`/admin/merchants/${m.id}/photos`);
      setDetailPhotos(r.data);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setDetailPhotosLoading(false); }
  };

  const saveName = async () => {
    try {
      const r = await api.post(`/admin/merchants/${detail.id}/edit-name`, editName);
      toast.success(r.data.message);
      setDetail((d) => ({ ...d, ...r.data.applied }));
      setMerchants((prev) => prev.map((x) => (x.id === detail.id ? { ...x, ...r.data.applied } : x)));
      setEditName(null);
    } catch (e) { toast.error(arabicError(e)); }
  };

  const sendAdminMsg = async () => {
    if (!adminMsg.trim() || adminMsgChannels.length === 0) return;
    try {
      const r = await api.post(`/admin/merchants/${detail.id}/message`, { message: adminMsg.trim(), channels: adminMsgChannels });
      resultToast(toast, r.data);
      setThread((prev) => [...prev, { id: `local-${Date.now()}`, direction: "to_merchant", message: adminMsg.trim(), ts: new Date().toISOString() }]);
      setAdminMsg("");
    } catch (e) { toast.error(arabicError(e)); }
  };

  const deleteMerchant = async (m) => {
    const label = [m.first_name, m.last_name].filter(Boolean).join(" ") || m.store_name || m.email;
    const msg = `هل أنت متأكد من حذف التاجر "${label}"؟\nسيتم حذف جميع صوره وحجوزاته ومحادثاته نهائياً. لا يمكن التراجع.`;
    if (!window.confirm(msg)) return;
    setDeletingId(m.id);
    try {
      const r = await api.delete(`/admin/merchants/${m.id}`);
      toast.success(r.data?.message || "تم حذف التاجر");
      if (detail?.id === m.id) setDetail(null);
      setMerchants((prev) => prev.filter((x) => x.id !== m.id));
    } catch (e) { toast.error(arabicError(e)); }
    finally { setDeletingId(null); }
  };

  const resetPassword = async (m) => {
    const label = [m.first_name, m.last_name].filter(Boolean).join(" ") || m.store_name;
    if (!window.confirm(`إنشاء كلمة مرور مؤقتة لـ"${label}"؟ كلمة مروره الحالية ستُلغى.`)) return;
    try {
      const r = await api.post(`/admin/merchants/${m.id}/reset-password`);
      setResetResult(r.data);
    } catch (e) { toast.error(arabicError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-merchants-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">التجار المعتمدون</h1>
        <p className="text-[#666666] mt-1">جميع التجار الفعّالين على المنصة</p>
        <button onClick={() => setBroadcast({ target: "all", city: "", emails: "", message: "", channels: [...DEFAULT_CHANNELS] })}
          data-testid="open-broadcast-btn"
          className="mt-3 h-11 px-5 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-2">
          <Send size={16} /> رسالة جماعية للتجار
        </button>
      </div>

      {broadcast && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setBroadcast(null)} dir="rtl">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="broadcast-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-extrabold text-[#333333]">رسالة جماعية من الإدارة</h3>
              <button onClick={() => setBroadcast(null)} className="w-9 h-9 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X size={18} /></button>
            </div>
            <p className="text-xs text-[#666666]">اختر وجهة الإرسال: إشعار داخل التطبيق، بريد إلكتروني، أو كلاهما (المرسل: imdad - إمداد)</p>
            <div className="flex gap-2">
              {[["all", "كل التجار"], ["city", "محافظة"], ["emails", "تجار محددون"]].map(([v, l]) => (
                <button key={v} onClick={() => setBroadcast((s) => ({ ...s, target: v }))}
                  data-testid={`broadcast-target-${v}`}
                  className={`flex-1 h-10 rounded-lg text-sm font-bold ${broadcast.target === v ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#09637E]"}`}>{l}</button>
              ))}
            </div>
            <ChannelPicker value={broadcast.channels || []} onChange={(v) => setBroadcast((s) => ({ ...s, channels: v }))} testid="broadcast-channels" />
            {broadcast.target === "city" && (
              <input value={broadcast.city} onChange={(e) => setBroadcast((s) => ({ ...s, city: e.target.value }))}
                placeholder="اسم المحافظة (مثال: دمشق)" data-testid="broadcast-city"
                className="h-11 w-full rounded-lg border border-[#7AB2B2] px-3 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
            )}
            {broadcast.target === "emails" && (
              <div className="space-y-2" data-testid="broadcast-trader-picker">
                <input value={broadcast.query || ""} onChange={(e) => setBroadcast((s) => ({ ...s, query: e.target.value }))}
                  placeholder="ابحث باسم التاجر أو المحل..." data-testid="broadcast-trader-search"
                  className="h-11 w-full rounded-lg border border-[#7AB2B2] px-3 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
                <div className="max-h-44 overflow-y-auto border border-[#EBF4F6] rounded-lg divide-y divide-[#EBF4F6]">
                  {merchants.filter((m) => {
                    const q = (broadcast.query || "").trim();
                    const label = `${m.first_name || ""} ${m.last_name || ""} ${m.store_name || ""}`;
                    return !q || label.includes(q);
                  }).slice(0, 200).map((m) => {
                    const checked = (broadcast.selected || []).includes(m.id);
                    return (
                      <label key={m.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-[#EBF4F6]" data-testid={`broadcast-trader-${m.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => setBroadcast((s) => ({
                          ...s, selected: checked ? (s.selected || []).filter((x) => x !== m.id) : [...(s.selected || []), m.id],
                        }))} />
                        <span className="font-bold text-[#333333]">{[m.first_name, m.last_name].filter(Boolean).join(" ") || "—"}</span>
                        <span className="text-xs text-[#666666]">{m.store_name}{m.city ? ` · ${m.city}` : ""}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="text-xs text-[#09637E] font-bold" data-testid="broadcast-selected-count">المحدد: {(broadcast.selected || []).length} تاجر</div>
              </div>
            )}
            <textarea value={broadcast.message} onChange={(e) => setBroadcast((s) => ({ ...s, message: e.target.value }))}
              rows={4} placeholder="نص الرسالة..." data-testid="broadcast-message"
              className="w-full rounded-lg border border-[#7AB2B2] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
            <button onClick={sendBroadcast} disabled={!broadcast.message.trim() || broadcasting || (broadcast.target === "emails" && !(broadcast.selected || []).length)}
              data-testid="broadcast-send"
              className="h-12 w-full rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold disabled:opacity-50">
              {broadcasting ? "جارٍ الإرسال..." : "إرسال"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-8 text-center text-[#666666]">جارٍ التحميل...</div>
      ) : merchants.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-12 text-center">
          <Users className="mx-auto text-[#7AB2B2] mb-3" size={48} />
          <p className="text-[#666666] text-lg">لا يوجد تجار معتمدون بعد</p>
        </div>
      ) : (
        <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden">
          <div className="md:hidden bg-[#EBF4F6] text-[#09637E] text-xs font-bold px-3 py-2 border-b border-[#7AB2B2]" data-testid="table-swipe-hint">← اسحب الجدول يميناً ويساراً لعرض بقية الأعمدة والإجراءات</div>
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead className="bg-[#EBF4F6]">
                <tr>
                  <th className="p-4 text-sm font-bold">البريد</th>
                  <th className="p-4 text-sm font-bold">الاسم الكامل</th>
                  <th className="p-4 text-sm font-bold">المحل</th>
                  <th className="p-4 text-sm font-bold">المحافظة</th>
                  <th className="p-4 text-sm font-bold">الهاتف</th>
                  <th className="p-4 text-sm font-bold">المواد</th>
                  <th className="p-4 text-sm font-bold">الصور</th>
                  <th className="p-4 text-sm font-bold whitespace-nowrap">الموظفون / الفروع</th>
                  <th className="p-4 text-sm font-bold">الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {merchants.map((m) => (
                  <tr key={m.id} className="border-t border-[#EBF4F6]" data-testid={`merchant-row-${m.id}`}>
                    <td className="p-4 text-[#333333]">{m.email}</td>
                    <td className="p-4 text-[#333333]">{[m.first_name, m.last_name].filter(Boolean).join(" ") || "-"}</td>
                    <td className="p-4 font-bold text-[#333333]">{m.store_name}</td>
                    <td className="p-4 text-[#666666]">{m.city}</td>
                    <td className="p-4 text-[#666666] text-sm" dir="ltr">{m.phone_number || "-"}</td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1 max-w-[240px]">
                        {(m.material_types || []).map((mt) => (
                          <span key={mt} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EBF4F6] text-[#09637E]">{mt}</span>
                        ))}
                        {(!m.material_types || m.material_types.length === 0) && <span className="text-xs text-[#666666]">-</span>}
                      </div>
                    </td>
                    <td className="p-4 text-[#09637E] font-bold">{m.photo_count ?? 0}</td>
                    <td className="p-4 text-sm text-[#333333] whitespace-nowrap tabular-nums" data-testid={`org-cell-${m.id}`}>
                      <div className="font-bold">{orgSummary(org[m.id])}</div>
                      {org[m.id]?.employees?.length > 0 && (
                        <div className="text-xs text-[#666666] max-w-[260px] truncate" title={org[m.id].employees.map((e) => e.name).join("، ")}>
                          {org[m.id].employees.map((e) => e.name).join("، ")}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => view(m)} data-testid={`view-merchant-${m.id}`}
                          className="h-10 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1 whitespace-nowrap">
                          <Eye size={16} /> التفاصيل
                        </button>
                        <button onClick={() => resetPassword(m)} data-testid={`reset-pw-${m.id}`}
                          className="h-10 px-3 rounded-lg border-2 border-[#088395] text-[#088395] hover:bg-[#EBF4F6] font-bold text-sm flex items-center gap-1 whitespace-nowrap">
                          <KeyRound size={16} /> كلمة مرور مؤقتة
                        </button>
                        <button onClick={() => deleteMerchant(m)} disabled={deletingId === m.id}
                          data-testid={`delete-merchant-${m.id}`}
                          className="h-10 px-3 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold text-sm flex items-center gap-1 whitespace-nowrap disabled:opacity-60">
                          <Trash2 size={16} /> {deletingId === m.id ? "..." : "حذف"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {resetResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetResult(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="reset-result-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#EBF4F6] text-[#09637E] flex items-center justify-center"><KeyRound size={24} /></div>
              <div>
                <h3 className="text-xl font-extrabold text-[#333333]">كلمة المرور المؤقتة</h3>
                <p className="text-sm text-[#666666]">{resetResult.email}</p>
              </div>
            </div>
            <div className="bg-[#EBF4F6] border border-[#7AB2B2] rounded-xl p-4 text-center mb-4">
              <div className="text-xs text-[#666666] mb-1">انسخها وأرسلها للتاجر — لن تظهر مرة أخرى</div>
              <div className="text-2xl font-extrabold text-[#09637E] tracking-wider font-mono" dir="ltr" data-testid="temp-password">{resetResult.temp_password}</div>
            </div>
            <button onClick={() => {
              navigator.clipboard?.writeText(resetResult.temp_password);
              toast.success("تم النسخ");
            }} className="w-full h-12 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold">
              نسخ كلمة المرور
            </button>
            <button onClick={() => setResetResult(null)} className="w-full mt-2 h-10 rounded-lg border-2 border-[#7AB2B2] text-[#09637E] font-bold">إغلاق</button>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="merchant-detail-modal">
            <div className="sticky top-0 bg-white border-b border-[#7AB2B2] p-5 flex items-center justify-between z-10">
              <div>
                <h3 className="text-xl font-extrabold text-[#333333]">{detail.store_name}</h3>
                <p className="text-sm text-[#666666]">
                  {[detail.first_name, detail.last_name].filter(Boolean).join(" ") || "—"} · {detail.email}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => deleteMerchant(detail)} disabled={deletingId === detail.id}
                  data-testid="delete-merchant-modal"
                  className="h-10 px-3 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold text-sm flex items-center gap-1 disabled:opacity-60">
                  <Trash2 size={16} /> حذف التاجر
                </button>
                <button onClick={() => setDetail(null)} data-testid="close-modal" className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
              </div>
            </div>

            <div className="p-5 space-y-5">
              <MerchantFeatureAccess merchantId={detail.id} />
              <MerchantOrgPanel org={org[detail.id]} merchantId={detail.id} />
              {/* إدارة التاجر: إحصائيات + تجربة + موقع + تعديل الاسم + رسالة */}
              {rich && (
                <div className="space-y-3" data-testid="merchant-rich-details">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-sm">
                    {[["المنتجات", rich.stats.total_photos], ["مخزون نشط", rich.stats.active_inventory],
                      ["عمليات بحث", rich.stats.searches], ["محادثات", rich.stats.conversations],
                      ["مبيعات مكتملة", rich.stats.sales], ["مشتريات مكتملة", rich.stats.purchases]].map(([l, v]) => (
                      <div key={l} className="bg-[#EBF4F6] rounded-lg p-2.5">
                        <div className="text-lg font-extrabold text-[#09637E]">{v}</div>
                        <div className="text-xs text-[#666666] font-bold">{l}</div>
                      </div>
                    ))}
                    <div className="bg-[#EBF4F6] rounded-lg p-2.5 col-span-2">
                      <div className={`text-sm font-extrabold ${rich.trial.active ? "text-green-600" : "text-red-500"}`}>
                        {rich.trial.active ? "تجربة مجانية فعّالة" : "التجربة منتهية"}
                      </div>
                      <div className="text-xs text-[#666666]">من {rich.trial.start?.slice(0, 10)} حتى {rich.trial.end?.slice(0, 10)} · {rich.subscription.label}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-xs text-[#666666]">
                    <span>تاريخ التسجيل: <b>{rich.user.created_at?.slice(0, 16).replace("T", " ")}</b></span>
                    {rich.stats.last_activity && <span>آخر نشاط: <b>{rich.stats.last_activity.slice(0, 16).replace("T", " ")}</b></span>}
                    {rich.user.location?.lat && (
                      <a href={`https://www.google.com/maps?q=${rich.user.location.lat},${rich.user.location.lng}`}
                        target="_blank" rel="noopener noreferrer" data-testid="merchant-maps-link"
                        className="inline-flex items-center gap-1 text-[#09637E] font-bold hover:underline">
                        <MapPin size={13} /> فتح موقع التاجر على الخريطة <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setEditName(editName ? null : {
                      first_name: detail.first_name || "", last_name: detail.last_name || "", store_name: detail.store_name || "",
                    })} data-testid="admin-edit-name-btn"
                      className="h-10 px-3 rounded-lg border-2 border-[#09637E] text-[#09637E] font-bold text-sm flex items-center gap-1 hover:bg-[#EBF4F6]">
                      <Pencil size={15} /> تعديل الاسم
                    </button>
                  </div>
                  {editName && (
                    <div className="bg-[#EBF4F6] rounded-lg p-3 flex gap-2 flex-wrap items-end" data-testid="admin-edit-name-form">
                      {[["first_name", "الاسم"], ["last_name", "الكنية"], ["store_name", "اسم المحل"]].map(([f, l]) => (
                        <div key={f}>
                          <label className="block text-xs font-bold text-[#666666] mb-1">{l}</label>
                          <input value={editName[f]} onChange={(e) => setEditName((s) => ({ ...s, [f]: e.target.value }))}
                            data-testid={`admin-edit-${f}`}
                            className="h-10 rounded-lg border border-[#7AB2B2] px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
                        </div>
                      ))}
                      <button onClick={saveName} data-testid="admin-save-name"
                        className="h-10 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm">حفظ (يُسجّل في سجل التدقيق)</button>
                    </div>
                  )}
                  <div className="bg-[#EBF4F6] rounded-lg p-3" data-testid="admin-message-box">
                    <label className="block text-xs font-bold text-[#666666] mb-1">محادثة الإدارة مع التاجر</label>
                    {thread.length > 0 && (
                      <div className="max-h-56 overflow-y-auto space-y-2 mb-3 pr-1" data-testid="admin-merchant-thread">
                        {thread.map((m) => {
                          const fromTrader = m.direction === "to_admin";
                          return (
                            <div key={m.id} className={`flex ${fromTrader ? "justify-end" : "justify-start"}`} data-testid={`admin-thread-msg-${m.id}`}>
                              <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${fromTrader ? "bg-white border border-[#7AB2B2] text-[#333333]" : "bg-[#09637E] text-white"}`}>
                                <div className="text-[10px] font-bold opacity-80 mb-0.5">{fromTrader ? detail.store_name || "التاجر" : "الإدارة"}</div>
                                <div className="whitespace-pre-wrap break-words">{m.message}</div>
                                <div className="text-[10px] opacity-70 mt-0.5">{m.ts ? new Date(m.ts).toLocaleString("ar-SY", { dateStyle: "short", timeStyle: "short" }) : ""}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="space-y-2">
                      <ChannelPicker value={adminMsgChannels} onChange={setAdminMsgChannels} testid="admin-message-channels" />
                      <div className="flex flex-wrap sm:flex-nowrap gap-2">
                      <input value={adminMsg} onChange={(e) => setAdminMsg(e.target.value)}
                        data-testid="admin-message-input" placeholder="اكتب رسالتك..."
                        className="h-11 flex-1 min-w-0 rounded-lg border border-[#7AB2B2] px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
                      <button onClick={sendAdminMsg} disabled={!adminMsg.trim() || adminMsgChannels.length === 0} data-testid="admin-send-message"
                        className="h-11 px-4 shrink-0 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1 disabled:opacity-50">
                        <Send size={15} /> إرسال
                      </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Merchant info grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-[#EBF4F6] rounded-lg p-3 flex items-start gap-3">
                  <MapPin className="text-[#09637E] shrink-0 mt-0.5" size={18} />
                  <div>
                    <div className="text-xs text-[#666666] font-bold">المحافظة</div>
                    <div className="text-[#333333] font-bold">{detail.city || "-"}</div>
                  </div>
                </div>
                <div className="bg-[#EBF4F6] rounded-lg p-3 flex items-start gap-3">
                  <Phone className="text-[#09637E] shrink-0 mt-0.5" size={18} />
                  <div>
                    <div className="text-xs text-[#666666] font-bold">رقم الهاتف</div>
                    <div className="text-[#333333] font-bold" dir="ltr">{detail.phone_number || "-"}</div>
                  </div>
                </div>
                <div className="bg-[#EBF4F6] rounded-lg p-3 flex items-start gap-3 md:col-span-2">
                  <Building2 className="text-[#09637E] shrink-0 mt-0.5" size={18} />
                  <div>
                    <div className="text-xs text-[#666666] font-bold">العنوان التفصيلي</div>
                    <div className="text-[#333333]">{detail.detailed_address || "-"}</div>
                  </div>
                </div>
                <div className="bg-[#EBF4F6] rounded-lg p-3 md:col-span-2">
                  <div className="text-xs text-[#666666] font-bold mb-2">أنواع المواد</div>
                  <div className="flex flex-wrap gap-2">
                    {(detail.material_types || []).length > 0 ? (
                      (detail.material_types || []).map((mt) => (
                        <span key={mt} className="text-xs font-bold px-3 py-1 rounded-full bg-[#09637E] text-white">{mt}</span>
                      ))
                    ) : <span className="text-[#666666] text-sm">لم يُحدّد</span>}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-3 text-[#333333]">الصور ({detailPhotos.length})</h4>
                {detailPhotosLoading ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => (
                      <div key={`photo-skeleton-${i}`} className="border border-[#7AB2B2] rounded-lg overflow-hidden">
                        <div className="w-full h-32 bg-gradient-to-br from-[#EBF4F6] to-[#7AB2B2]/40 animate-pulse" />
                        <div className="p-2"><div className="h-3 bg-[#EBF4F6] rounded animate-pulse" /></div>
                      </div>
                    ))}
                  </div>
                ) : detailPhotos.length === 0 ? (
                  <p className="text-[#666666] text-center py-8">لم يرفع التاجر أي صور بعد</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {detailPhotos.map((p) => (
                      <div key={p.id} className="border border-[#7AB2B2] rounded-lg overflow-hidden">
                        <div className="w-full h-32">
                          <AdminPhotoThumb
                            photoId={p.id}
                            alt={p.display_name || ""}
                            onClick={(src) => setLightbox({ ...p, image_url: src })}
                            testid={`merchant-photo-thumb-${p.id}`}
                            lazy={false}
                          />
                        </div>
                        <div className="p-2 text-xs">
                          <div className="font-bold text-[#333333] truncate">{p.display_name || `${p.size} · ${p.color || ""}`}</div>
                          <div className="text-[#09637E] font-bold" dir="ltr">${(p.price_usd ?? p.price ?? 0).toFixed(2)} / م</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {lightbox && lightbox.image_url && (
        <div
          className="fixed inset-0 bg-black/90 z-[110] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="merchant-photo-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-[#333333] font-extrabold text-xl"
            aria-label="إغلاق"
          >✕</button>
          <img
            src={lightbox.image_url}
            alt={lightbox.display_name || ""}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AdminMerchants;
